/**
 * Deduplication of the practice proposal analytics (TANAI-2616, spec D11):
 * - PRACTICE_PROPOSAL_SHOWN once per proposal and tab → `sessionStorage`, which a reload keeps and
 *   another tab does not share;
 * - PRACTICE_PROPOSAL_STARTED once per attempt → `localStorage`, because every click on the card
 *   opens a NEW launcher tab, and «Continuar» resumes an attempt that was already started.
 */
import type { PracticeProposalTargetType } from '../types/practiceProposal';

const SHOWN_PROPOSALS_KEY = 'tangerine_practice_proposals_shown';
const STARTED_ATTEMPTS_KEY = 'tangerine_practice_attempts_started';
// Only the latest ids matter (a conversation proposes 3 practices at most): the cap keeps the
// lists from growing for the whole life of the browser.
const MAX_REMEMBERED_IDS = 50;

export type PracticeProposalShownPayload = {
  conversation_id: string;
  proposal_id: string;
  target_type: PracticeProposalTargetType;
};

export type PracticeProposalStartedPayload = PracticeProposalShownPayload & {
  attempt_id: string;
};

function readIds(storage: Storage, key: string): string[] {
  try {
    const parsed: unknown = JSON.parse(storage.getItem(key) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

/** Remembers `id` and tells whether it was new. Storage failures never block the event. */
function rememberOnce(getStorage: () => Storage, key: string, id: string): boolean {
  try {
    const storage = getStorage();
    const ids = readIds(storage, key);
    if (ids.includes(id)) return false;
    storage.setItem(key, JSON.stringify([...ids, id].slice(-MAX_REMEMBERED_IDS)));
    return true;
  } catch {
    return true;
  }
}

export function shouldTrackProposalShown(proposalId: string): boolean {
  return rememberOnce(() => sessionStorage, SHOWN_PROPOSALS_KEY, proposalId);
}

export function shouldTrackAttemptStarted(attemptId: string): boolean {
  return rememberOnce(() => localStorage, STARTED_ATTEMPTS_KEY, attemptId);
}
